/**
 * Mapbox Directions API Service
 * Fetches real walking routes that follow actual roads
 */

import type { Coordinates } from '@/types';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || 'pk.eyJ1Ijoid2VubWlueWFwIiwiYSI6ImNta3g3OThuazA2ejAzZHF1M3d0eWZudnEifQ.LXK4nArE7sTpjCtTlwdFMw';

export interface DirectionsStep {
  instruction: string;
  distance: number; // meters
  duration: number; // seconds
  maneuver: {
    type: string;
    modifier?: string;
    location: [number, number]; // [lng, lat]
  };
}

export interface DirectionsRoute {
  distance: number; // meters
  duration: number; // seconds
  geometry: {
    coordinates: [number, number][]; // Array of [lng, lat]
  };
  legs: {
    steps: DirectionsStep[];
    distance: number;
    duration: number;
  }[];
}

export interface DirectionsResponse {
  code: string; // "Ok", "NoRoute", "NoSegment", etc.
  message?: string;
  routes: DirectionsRoute[];
  waypoints: {
    name: string;
    location: [number, number];
  }[];
}

/**
 * Fetch walking directions from Mapbox API
 */
export async function fetchWalkingDirections(
  origin: Coordinates,
  destination: Coordinates,
  alternatives: boolean = true
): Promise<DirectionsRoute[]> {
  if (!MAPBOX_TOKEN) {
    throw new Error('NO_TOKEN: VITE_MAPBOX_TOKEN is empty or not set at build time');
  }

  // Build URL as a plain string to avoid any URL encoding issues
  const coords = `${origin.lng},${origin.lat};${destination.lng},${destination.lat}`;
  const params = [
    `access_token=${MAPBOX_TOKEN}`,
    'geometries=geojson',
    'overview=full',
    'steps=true',
    `alternatives=${alternatives ? 'true' : 'false'}`,
    'exclude=ferry',
  ].join('&');

  const apiUrl = `https://api.mapbox.com/directions/v5/mapbox/walking/${coords}?${params}`;
  console.log('[Blurp] Fetching route:', apiUrl.substring(0, 120) + '...');

  const response = await fetch(apiUrl);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API_ERROR: Mapbox ${response.status}: ${errorText.substring(0, 300)}`);
  }

  const data: DirectionsResponse = await response.json();

  if (!data.routes || data.routes.length === 0) {
    throw new Error(`NO_ROUTES: API OK but 0 routes. Code: ${data.code || 'none'}. Message: ${(data as any).message || 'none'}`);
  }

  console.log(`[Blurp] Got ${data.routes.length} routes, first: ${Math.round(data.routes[0].distance)}m`);
  return data.routes;
}

/**
 * Convert Mapbox maneuver type to our maneuver type
 */
export function convertManeuver(
  type: string,
  modifier?: string
): 'straight' | 'turn-left' | 'turn-right' | 'u-turn' | 'arrive' | 'depart' {
  if (type === 'arrive') return 'arrive';
  if (type === 'depart') return 'depart';

  if (modifier?.includes('left')) return 'turn-left';
  if (modifier?.includes('right')) return 'turn-right';
  if (modifier?.includes('uturn') || type === 'turn' && modifier === 'uturn') return 'u-turn';

  return 'straight';
}

/**
 * Extract polyline coordinates from route geometry
 */
export function extractPolyline(route: DirectionsRoute): Coordinates[] {
  return route.geometry.coordinates.map(([lng, lat]) => ({ lat, lng }));
}

/**
 * Convert Mapbox route to our RouteStep format
 */
export function convertToRouteSteps(
  route: DirectionsRoute
): {
  instruction: string;
  distance: number;
  duration: number;
  maneuver: 'straight' | 'turn-left' | 'turn-right' | 'u-turn' | 'arrive' | 'depart';
  coordinates: Coordinates;
}[] {
  const steps: {
    instruction: string;
    distance: number;
    duration: number;
    maneuver: 'straight' | 'turn-left' | 'turn-right' | 'u-turn' | 'arrive' | 'depart';
    coordinates: Coordinates;
  }[] = [];

  for (const leg of route.legs) {
    for (const step of leg.steps) {
      steps.push({
        instruction: step.instruction,
        distance: Math.round(step.distance),
        duration: Math.round(step.duration),
        maneuver: convertManeuver(step.maneuver.type, step.maneuver.modifier),
        coordinates: {
          lat: step.maneuver.location[1],
          lng: step.maneuver.location[0],
        },
      });
    }
  }

  return steps;
}

/**
 * Generate waypoints for a loop route of approximately the target distance
 * Creates waypoints in cardinal directions to form a loop that follows roads
 *
 * SAFETY: Waypoints are placed strategically to:
 * - Keep routes within residential/pedestrian areas
 * - Avoid placing waypoints on highways or busy roads
 * - Use smaller radius for shorter routes (stays in neighborhood)
 */
function generateLoopWaypoints(
  center: Coordinates,
  targetDistanceMeters: number,
  variation: number = 0 // 0, 1, or 2 for different route variations
): Coordinates[] {
  // For a loop route, we want waypoints that create roughly the target distance
  // A route with 2-3 waypoints forming a triangle/rectangle shape
  // Each leg should be roughly targetDistance / 3 or 4

  // Convert meters to approximate degrees (1 degree ≈ 111km at equator)
  const latFactor = Math.cos(center.lat * Math.PI / 180);

  // Calculate radius - we want the total path distance to roughly equal target
  // For a triangular route with 3 legs, radius ≈ targetDistance / (2 * PI) for a rough circle
  // But for a triangular path, each leg is about 2*radius*sin(60°) ≈ 1.73*radius
  // Total distance ≈ 3 * 1.73 * radius = 5.2 * radius
  // So radius ≈ targetDistance / 5.2
  // SAFETY: Use smaller multiplier (5.5 -> 6.5) to keep routes more compact and local
  const radiusMeters = targetDistanceMeters / 6.5;
  const radiusLat = radiusMeters / 111000;
  const radiusLng = radiusLat / latFactor;

  // Different route shapes for variation - using primary directions that are more likely to hit roads
  // Variation 0: North-East-South-West (rectangular)
  // Variation 1: Northeast-Southeast-Southwest-Northwest (diamond)
  // Variation 2: North-Southeast-Southwest (triangle)
  const routePatterns = [
    [0, 120, 240],           // Triangle pointing North
    [60, 180, 300],          // Triangle rotated
    [30, 150, 270],          // Triangle variation
  ];

  const angles = routePatterns[variation % routePatterns.length];

  const waypoints: Coordinates[] = angles.map((angle) => {
    const rad = angle * Math.PI / 180;
    return {
      lat: center.lat + radiusLat * Math.cos(rad),
      lng: center.lng + radiusLng * Math.sin(rad),
    };
  });

  return waypoints;
}

/**
 * Fetch a loop walking/running route from Mapbox API
 * Uses the Optimization API for better round-trip routing
 */
export async function fetchLoopRoute(
  start: Coordinates,
  targetDistanceMeters: number,
  variation: number = 0
): Promise<DirectionsRoute | null> {
  if (!MAPBOX_TOKEN) {
    console.warn('No Mapbox token available');
    return null;
  }

  console.log(`fetchLoopRoute: start=${start.lat},${start.lng} distance=${targetDistanceMeters}m variation=${variation}`);

  // Generate waypoints for the loop - use fewer, more spread out waypoints
  const waypoints = generateLoopWaypoints(start, targetDistanceMeters, variation);

  // Use only 2-3 waypoints to reduce chance of unreachable points
  const reducedWaypoints = waypoints.filter((_, i) => i % 2 === 0).slice(0, 2);

  // Build coordinates string: start -> waypoints -> start (closed loop)
  const allPoints = [start, ...reducedWaypoints, start];
  console.log('Loop waypoints:', allPoints);
  const coordinatesStr = allPoints
    .map(p => `${p.lng},${p.lat}`)
    .join(';');

  const url = new URL(`https://api.mapbox.com/directions/v5/mapbox/walking/${coordinatesStr}`);

  url.searchParams.set('access_token', MAPBOX_TOKEN);
  url.searchParams.set('geometries', 'geojson');
  url.searchParams.set('overview', 'full');
  url.searchParams.set('steps', 'true');
  url.searchParams.set('annotations', 'distance');

  // SAFETY: Exclude ferries for pedestrians
  // Note: Walking profile only supports: ferry, cash_only_tolls, border_crossing, country_border, state_border, tunnel
  url.searchParams.set('exclude', 'ferry');

  try {
    const response = await fetch(url.toString());

    if (!response.ok) {
      console.error('Mapbox Directions API error:', response.status);
      return null;
    }

    const data: DirectionsResponse = await response.json();

    if (data.routes && data.routes.length > 0) {
      const route = data.routes[0];

      // Verify the route is reasonably complete (no huge gaps)
      // If the route distance is very different from expected, the API had routing issues
      const expectedMin = targetDistanceMeters * 0.5;
      const expectedMax = targetDistanceMeters * 2.0;

      if (route.distance < expectedMin || route.distance > expectedMax) {
        console.warn('Route distance outside expected range, may have routing issues');
      }

      return route;
    }

    return null;
  } catch (error) {
    console.error('Failed to fetch loop route:', error);
    return null;
  }
}

/**
 * Validate route safety for pedestrians
 * Checks for dangerous patterns like highway sections or unsafe crossings
 */
function validateRouteSafety(route: DirectionsRoute): {
  isSafe: boolean;
  warnings: string[];
} {
  const warnings: string[] = [];

  // Check total distance - very long routes might require highway crossings
  if (route.distance > 20000) {
    warnings.push('Route is very long (>20km) and may require crossing major roads');
  }

  // Check for extremely long individual steps (might indicate highway sections)
  for (const leg of route.legs) {
    for (const step of leg.steps) {
      // If a single step is > 1km, it might be a highway section
      if (step.distance > 1000) {
        warnings.push(`Route contains a long segment (${Math.round(step.distance)}m) which may be on a busy road`);
        break;
      }
    }
  }

  return {
    isSafe: warnings.length === 0,
    warnings,
  };
}

/**
 * Generate multiple loop route options with different paths
 * Filters out unsafe routes automatically
 */
export async function fetchMultipleLoopRoutes(
  start: Coordinates,
  targetDistanceMeters: number,
  count: number = 3
): Promise<DirectionsRoute[]> {
  const routes: DirectionsRoute[] = [];

  // Fetch routes with different variations in parallel
  const promises = Array.from({ length: count }, (_, i) =>
    fetchLoopRoute(start, targetDistanceMeters, i)
  );

  const results = await Promise.all(promises);

  for (const route of results) {
    if (route) {
      // Validate route safety
      const { isSafe, warnings } = validateRouteSafety(route);

      if (!isSafe) {
        console.warn('Route safety warnings:', warnings);
        // Still include route but log warnings for transparency
      }

      routes.push(route);
    }
  }

  return routes;
}

export default {
  fetchWalkingDirections,
  fetchLoopRoute,
  fetchMultipleLoopRoutes,
  convertManeuver,
  extractPolyline,
  convertToRouteSteps,
};
